import { css } from "@emotion/react";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import useSwitch from "@react-hook/switch";
import { getRouteApi, Link } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { type FC, Suspense } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import { BarLoading } from "@/components/Loading/BarLoading";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import {
  ChannelQueriesContext,
  ChannelQueriesContextProvider,
} from "@/pages/channels/pages/detail/channelQueriesContext";
import { ChannelNameEdit } from "@/pages/channels/pages/detail/components/ChannelNameEdit";
import { DisconnectModal } from "@/pages/channels/pages/detail/components/DisconnectModal";
import { FailedConnectModal } from "@/pages/channels/pages/detail/components/FailedConnectModal";
import { ReauthorizeButton } from "@/pages/channels/pages/detail/components/ReauthorizeButton";
import { PageTitle } from "@/pages/components/PageTitle";

const route = getRouteApi("/channels/$type/$id");

const FacebookDetailPageInternal: FC = () => {
  const { t } = useTranslation();
  const { id } = route.useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [openFailedConnectModal, toggleFailedConnectModal] = useSwitch();
  const [openDisconnectModal, toggleDisconnectModal] = useSwitch();

  const { channel } = ChannelQueriesContext.useData();
  const updateChannelMutation = interlude.channel.useUpdateChannel(
    {
      params: {
        channelId: channel.id,
      },
    },
    {
      onSuccess() {
        toggleDisconnectModal.off();
        enqueueSnackbar(
          t("page.channel.fb.disconnect.toast.success", {
            channelName: channel.name,
          }),
          {
            variant: "success",
          },
        );
      },
    },
  );

  const handleChannelDisconnect = () => {
    updateChannelMutation.mutate({
      status: "disconnected",
    });
  };

  return (
    <>
      <Stack gap="8px">
        <Breadcrumbs aria-label="breadcrumb" separator=">">
          <Link to="/channels" css={css({ textDecoration: "none" })}>
            <Typography variant="body" color={theme.colors.staticFgBody}>
              {t("channels.title")}
            </Typography>
          </Link>
          <Link
            to="/channels/$type/$id"
            params={{
              type: "fb",
              id,
            }}
            css={css({ textDecoration: "none" })}
          >
            <Typography variant="body" color={theme.colors.staticFgNote}>
              {t("resource.channel.breadcrumb.fb.label")}
            </Typography>
          </Link>
        </Breadcrumbs>
        <PageTitle>{t("resource.channel.header.fb.label")}</PageTitle>
        <Stack gap="24px" css={css({ maxWidth: "512px" })}>
          <Stack gap="8px">
            <Typography
              variant="h3"
              fontWeight="medium"
              color={theme.colors.staticFgTitle}
            >
              {t("resource.channel.subHeader.fb.label")}
            </Typography>
            <ChannelNameEdit />
          </Stack>
          <Stack gap="8px">
            <Typography
              variant="h3"
              fontWeight="medium"
              color={theme.colors.staticFgTitle}
            >
              {t("resource.channel.update.credential.fb.header")}
            </Typography>
            <Typography variant="note" color={theme.colors.staticFgNote}>
              {t("resource.channel.update.credential.expired.fb.description")}
            </Typography>
            {channel.status === "token-expired" ? (
              <InfoBox
                variant="error"
                title={t(
                  "resource.channel.update.credential.expired.fb.header",
                )}
                description={t(
                  "resource.channel.update.credential.expired.fb.description",
                )}
              />
            ) : null}
            <ReauthorizeButton
              variant="plain"
              disabled={channel.status === "disconnected"}
            >
              {t("resource.channel.update.credential.refresh.button.label")}
            </ReauthorizeButton>
          </Stack>
          <Stack gap="8px">
            <Typography
              variant="h3"
              fontWeight="medium"
              color={theme.colors.staticFgTitle}
            >
              {t("resource.channel.update.connection.header")}
            </Typography>
            <Typography variant="note" color={theme.colors.staticFgNote}>
              {t("resource.channel.update.connection.fb.description")}
            </Typography>
            {channel.status !== "disconnected" ? (
              <Button
                variant="plain"
                color="error"
                onClick={toggleDisconnectModal.on}
              >
                {t("common.disconnect")}
              </Button>
            ) : (
              <ReauthorizeButton variant="plain">
                {t("common.connect")}
              </ReauthorizeButton>
            )}
          </Stack>
        </Stack>
      </Stack>
      <FailedConnectModal
        channelType="fb"
        open={openFailedConnectModal}
        onClose={toggleFailedConnectModal.off}
        onConfirm={toggleFailedConnectModal.off}
      />
      <DisconnectModal
        channelType="fb"
        open={openDisconnectModal}
        isLoading={updateChannelMutation.isPending}
        onClose={toggleDisconnectModal.off}
        onConfirm={handleChannelDisconnect}
      />
    </>
  );
};

export const FacebookDetailPage: React.FC = () => {
  return (
    <Suspense fallback={<BarLoading />}>
      <ChannelQueriesContextProvider>
        <FacebookDetailPageInternal />
      </ChannelQueriesContextProvider>
    </Suspense>
  );
};
